import { sendToApi } from 'api/client'
import { MetricSnapshot } from 'hooks/queries/externalIntegrations/types'
import { PostLocation, PostTag, SourceMaterialScraping } from 'types'
import { MailchimpUser } from 'components/EmailBlock/types'

export type ActivationProviderKey =
  | 'instagram'
  | 'linkedin'
  | 'x'
  | 'tiktok'
  | 'youtube'
  | 'word_press'
  | 'mailchimp'
  | 'facebook'
  | 'google_business_profile'

export type ActivationProvider = {
  provider: ActivationProviderKey
  connected: boolean
  posts_waiting_count: number
  display_name: string
  connect_strategy: 'oauth' | 'custom_form'
}

export type ActivationSummary = { providers: ActivationProvider[] }

export enum ExternalIntegrationEnum {
  WordPress = 'word_press',
  X = 'x',
  LinkedIn = 'linkedin',
  Instagram = 'instagram',
  Facebook = 'facebook',
  TikTok = 'tiktok',
  YouTube = 'youtube',
  Mailchimp = 'mailchimp',
  Google = 'google',
  GoogleDrive = 'google_drive',
  GoogleBusinessProfile = 'google_business_profile',
  MetaAds = 'meta_ads',
  GoHighLevel = 'go_high_level',
  Wix = 'wix',
  Shopify = 'shopify',
  GoogleAds = 'google_ads',
}

export interface BaseExternalIntegration {
  id: number
  provider_id: string
  name: string
  picture: string
  account_type: 'page' | 'profile' | 'analytics' | 'drive'
  created_at: string
  updated_at: string
  scopes: string[]
  pending_or_started_source_material_scraping?: SourceMaterialScraping
  last_heartbeat_checked_at: string | null
  last_disconnected_email_sent_at: string | null
  disconnected_email_count: number
  is_active: boolean
  brain_trend_performance_latest_metric_snapshot?: MetricSnapshot | null
  brain_trend_performance_last_week_metric_snapshot?: MetricSnapshot | null
  type: 'ExternalIntegration'
  brain_channel_insights_bid?: string | null
  posts_fetching_bid?: string | null
  requires_purchase?: boolean
}

export const CROSS_POST_SUPPORTED_PROVIDERS = [
  ExternalIntegrationEnum.Instagram,
  ExternalIntegrationEnum.Facebook,
  ExternalIntegrationEnum.LinkedIn,
  ExternalIntegrationEnum.X,
  ExternalIntegrationEnum.GoogleBusinessProfile,
]

export type ContentIntegrationEnum = Exclude<
  ExternalIntegrationEnum,
  | ExternalIntegrationEnum.Google
  | ExternalIntegrationEnum.GoogleDrive
  | ExternalIntegrationEnum.GoogleBusinessProfile
  | ExternalIntegrationEnum.MetaAds
  | ExternalIntegrationEnum.GoogleAds
>

export interface LinkedInIntegration extends BaseExternalIntegration {
  provider: ExternalIntegrationEnum.LinkedIn
  additional_data?: LinkedInProfile
}

export interface InstagramIntegration extends BaseExternalIntegration {
  provider: ExternalIntegrationEnum.Instagram
  additional_data?: InstagramProfile
}

export interface FacebookIntegration extends BaseExternalIntegration {
  provider: ExternalIntegrationEnum.Facebook
  additional_data?: FacebookProfile
}

export interface XIntegration extends BaseExternalIntegration {
  provider: ExternalIntegrationEnum.X
  additional_data?: XProfile
}

export interface TikTokIntegration extends BaseExternalIntegration {
  provider: ExternalIntegrationEnum.TikTok
  additional_data?: TikTokProfile
}

export interface WordPressIntegration extends BaseExternalIntegration {
  provider: ExternalIntegrationEnum.WordPress
  additional_data?: WordPressProfile
}

export interface YouTubeIntegration extends BaseExternalIntegration {
  provider: ExternalIntegrationEnum.YouTube
  additional_data?: YouTubeProfile
}

export interface MailchimpIntegration extends BaseExternalIntegration {
  provider: ExternalIntegrationEnum.Mailchimp
  additional_data?: MailchimpProfile
}

export interface GoogleIntegration extends BaseExternalIntegration {
  provider: ExternalIntegrationEnum.Google
  additional_data?: {
    default_uri?: string
  }
}

export interface GoogleDriveIntegration extends BaseExternalIntegration {
  provider: ExternalIntegrationEnum.GoogleDrive
  additional_data?: Record<string, never>
}

export interface GoogleBusinessProfileIntegration extends BaseExternalIntegration {
  provider: ExternalIntegrationEnum.GoogleBusinessProfile
  additional_data?: Record<string, never>
}

export interface MetaAdsIntegration extends BaseExternalIntegration {
  provider: ExternalIntegrationEnum.MetaAds
  additional_data?: MetaAdsProfile
}

export interface GoHighLevelIntegration extends BaseExternalIntegration {
  provider: ExternalIntegrationEnum.GoHighLevel
  additional_data?: GoHighLevelProfile
}

export type ExternalIntegration =
  | LinkedInIntegration
  | InstagramIntegration
  | XIntegration
  | FacebookIntegration
  | TikTokIntegration
  | WordPressIntegration
  | YouTubeIntegration
  | MailchimpIntegration
  | GoogleIntegration
  | GoogleDriveIntegration
  | GoogleBusinessProfileIntegration
  | MetaAdsIntegration
  | GoHighLevelIntegration
  | WixIntegration
  | ShopifyIntegration
  | GoogleAdsIntegration

export type ExternalIntegrationWithMetrics = ExternalIntegration & {
  latest_metric_snapshots: MetricSnapshot[]
}

export type Action = 'refreshAccount' | 'connectAccount'

export interface BaseState {
  timestamp: number
  nonce: number
}

export interface RefreshAccountState extends BaseState {
  externalIntegrationId: number
}

export const ExternalIntegrationsClient = {
  getExternalIntegrations: async ({ workspaceId }: { workspaceId: number }) => {
    return sendToApi<{ external_integrations: ExternalIntegration[] }>(`/w/${workspaceId}/external_integrations`)
  },
  getExternalIntegrationsWithMetrics: async ({ workspaceId }: { workspaceId: number }) => {
    return sendToApi<{ external_integrations: ExternalIntegrationWithMetrics[] }>(
      `/w/${workspaceId}/external_integrations/index_with_metrics`,
    )
  },
  deleteExternalIntegration: async ({ workspaceId, id }: { workspaceId: number; id: number }) => {
    const res = await sendToApi<void>(`/w/${workspaceId}/external_integrations/${id}`, { method: 'delete' })

    if (res.type === 'error') {
      throw new Error(res.statusText || 'API Error')
    }

    return res
  },
  bulkDestroyExternalIntegrations: async ({ workspaceId, ids }: { workspaceId: number; ids: number[] }) => {
    const res = await sendToApi<void>(`/w/${workspaceId}/external_integrations/bulk_destroy`, {
      method: 'DELETE',
      body: { ids },
    })

    if (res.type === 'error') {
      throw new Error(res.statusText || 'API Error')
    }

    return res
  },
  updateExternalIntegration: async ({
    workspaceId,
    id,
    params,
  }: {
    workspaceId: number
    id: number
    params: {
      additional_data?:
        | Partial<LinkedInProfile>
        | Partial<InstagramProfile>
        | Partial<FacebookProfile>
        | Partial<XProfile>
        | Partial<TikTokProfile>
        | Partial<WordPressProfile>
        | Partial<YouTubeProfile>
        | Partial<MailchimpProfile>
        | Partial<MetaAdsProfile>
        | Partial<GoHighLevelProfile>
        | { default_uri?: string } // For Google integration
        | Partial<GoogleAdsAdditionalData>
    }
  }) => {
    const res = await sendToApi<{ external_integration: ExternalIntegration }>(
      `/w/${workspaceId}/external_integrations/${id}`,
      {
        body: params,
        method: 'PATCH',
      },
    )
    return res
  },
  importExistingCampaigns: async ({ workspaceId, id }: { workspaceId: number; id: number }) => {
    return sendToApi<void>(`/w/${workspaceId}/external_integrations/${id}/import_existing_campaigns`, {
      method: 'post',
    })
  },
  createExternalIntegration: async ({ workspaceId, params }: { workspaceId: number; params: any }) => {
    const res = await sendToApi<{ external_integration: ExternalIntegration }>(
      `/w/${workspaceId}/external_integrations`,
      {
        body: params,
        method: 'post',
      },
    )
    return res
  },
  searchLocations: async ({
    workspaceId,
    integrationId,
    query,
  }: {
    workspaceId: number
    integrationId: number
    query: string
  }) => {
    return sendToApi<{ locations: PostLocation[] }>(
      `/w/${workspaceId}/external_integrations/${integrationId}/search_locations?q=${encodeURIComponent(query)}`,
    )
  },
  validateLocation: async ({
    workspaceId,
    integrationId,
    geoUrn,
  }: {
    workspaceId: number
    integrationId: number
    geoUrn: string
  }) => {
    return sendToApi<{ total: number; meets_minimum: boolean }>(
      `/w/${workspaceId}/external_integrations/${integrationId}/validate_location?geo_urn=${encodeURIComponent(geoUrn)}`,
    )
  },
  searchUsers: async ({
    workspaceId,
    integrationId,
    query,
  }: {
    workspaceId: number
    integrationId: number
    query: string
  }) => {
    return sendToApi<{ users: PostTag[] }>(
      `/w/${workspaceId}/external_integrations/${integrationId}/search_users?q=${encodeURIComponent(query)}`,
    )
  },
  getActivationSummary: async ({ workspaceId }: { workspaceId: number }) => {
    return sendToApi<ActivationProvider[]>(`/w/${workspaceId}/integrations/activation_summary`)
  },
}

export interface LinkedInProfile {
  name: string
  picture: string
  sub: string
  email?: string
}

export interface InstagramProfile {
  username: string
  profile_picture_url: string
  id: string
  name?: string
}

export interface XProfile {
  id: string
  name: string
  username: string
  profile_image_url: string
}
export interface WordPressUser {
  id: number
  name: string
  slug: string
}

export interface WordPressProfile {
  url: string
  wordpress_categories?: Array<{
    id: number
    name: string
    slug: string
    description?: string
  }>
  wordpress_users?: WordPressUser[]
  selected_author_id?: number
}

export interface FacebookProfile {
  name: string
  id: string
  picture: {
    data: {
      url: string
    }
  }
}

export interface TikTokProfile {
  username: string
}

export interface YouTubeProfile {
  username: string
}

export interface MailchimpProfile extends Partial<MailchimpUser> {
  username: string
  account_name: string
  avatar_url: string
}

export enum MetaAdsAccountStatus {
  Active = 1,
  Disabled = 2,
  Unsettled = 3,
  PendingRiskReview = 4,
  PendingSettlement = 5,
  InGracePeriod = 6,
  PendingClosure = 7,
  Closed = 8,
  AnyActive = 9,
  AnyClosed = 101,
}

export interface MetaAdsProfile {
  account_status?: MetaAdsAccountStatus | null
  currency?: string | null
  timezone_name?: string | null
  amount_spent?: string | null
  business_name?: string | null
  business_id?: string | null
  facebook_account?: {
    name?: string | null
    picture?: string | null
    provider?: string | null
    provider_id?: string | null
    account_type?: string | null
    additional_data?: {
      category?: string | null
      fan_count?: number | null
      verification_status?: string | null
    } | null
  } | null
  // Use facebook_account.provider_id instead
  facebook_page_id?: string | null
  instagram_account?: {
    name?: string | null
    picture?: string | null
    provider?: string | null
    provider_id?: string | null
    account_type?: string | null
    additional_data?: {
      username?: string | null
      followers_count?: number | null
    } | null
  } | null
  // Use instagram_account.provider_id instead
  instagram_account_id?: string | null
}

export interface GoHighLevelProfile {
  user_id: string
  user_name: string
  user_email: string
  location_id: string
  company_id: string
  user_type: string
  user_permissions: Record<string, boolean>
  user_roles: {
    role: string
    type: string
    locationIds: string[]
  }
  scope: string
  approved_locations: string[] | null
  go_high_level_categories: GoHighLevelCategory[]
  categories_last_fetched: string
  blogs?: GoHighLevelBlog[]
  authors?: GoHighLevelAuthor[]
  blogs_last_fetched?: string
  authors_last_fetched?: string
  selected_blog_id?: string
  selected_author_id?: string
}

export interface GoHighLevelCategory {
  id: string
  name: string
  slug: string
  location_id: string
  canonical_link?: string
  updated_at: string
}

export interface GoHighLevelBlog {
  id: string
  name: string
  url?: string
  location_id?: string
  updated_at?: string
}

export interface GoHighLevelAuthor {
  id: string
  name: string
  email?: string
  location_id?: string
  updated_at?: string
  canonical_link?: string
}

export interface WixProfile {
  site_id: string
  site_display_name: string
  instance_id: string
  selected_author_id?: string
  wix_categories?: Array<{
    id: string
    name: string
    label?: string
  }>
  wix_tags?: Array<{
    id: string
    name: string
    label?: string
  }>
  wix_members?: Array<{
    id: string
    name: string
    slug?: string
    photo_url?: string
  }>
}

export interface WixBlog {
  id: string
  name: string
  url: string
}

export interface WixIntegration extends BaseExternalIntegration {
  provider: ExternalIntegrationEnum.Wix
  additional_data?: {
    profile?: WixProfile
    blogs?: WixBlog[]
  }
}

export interface ShopifyIntegration extends BaseExternalIntegration {
  provider: ExternalIntegrationEnum.Shopify
  additional_data?: ShopifyProfile
}

export interface GoogleAdsCampaignSummary {
  id: string
  name: string
}

export interface GoogleAdsAdditionalData {
  conversion_tracking_confirmed?: boolean
  has_existing_campaigns?: boolean
  manage_existing_campaigns?: boolean
  existing_campaigns_summary?: GoogleAdsCampaignSummary[]
}

export interface GoogleAdsIntegration extends BaseExternalIntegration {
  provider: ExternalIntegrationEnum.GoogleAds
  additional_data?: GoogleAdsAdditionalData
}

export interface ShopifyProfile {
  shop_domain: string
  scopes: string[]
  selected_blog_id?: string
  selected_author_id?: string
  domain?: string
  shopify_blogs?: Array<{
    id: string
    created_at: string
    handle: string
    name: string
    updated_at: string
    commentable: string
  }>
  shopify_authors?: Array<{
    id: string
    name: string
    email?: string
  }>
}
